import React, { useCallback, useMemo, useRef } from "react";

import { faSearch, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Fragment, Node as ProseNode, Slice } from "prosemirror-model";
import { EditorState, Plugin } from "prosemirror-state";
import { Form } from "semantic-ui-react";

import { multiScore } from "~/lib/fzy";
import { useForwardedRef } from "~/lib/hooks";
import { Extension } from "~/lib/prose";
import Tag from "~/lib/prose/nodes/tag";
import TextNode from "~/lib/prose/nodes/text";
import { GlobalPlaceholder } from "~/lib/prose/plugins";
import ProseReact, { EditorConfig } from "~/lib/prose/prose-react";

import "./css/SearchForm.css";

const searchOutputConfig = new EditorConfig({
	nodes: { doc: { content: "inline*" } },
	extensions: [new TextNode(), new Tag({ showToolbar: false, clickable: false })],
});
export const searchSchema = searchOutputConfig.schema;
export type SearchDoc = ProseNode;

/**
 * Convert a search query document to a string for easy storage, including using it
 * as a query parameter in a URL. The format, simply put, works like this:
 *
 * ```plaintext
 * #this-is-a-tag# \#this-is-not-a-tag \\#this-tag-has-a-backslash-in-front-of-it#"
 * ```
 *
 * @see {@link searchStringToDoc}
 */
export function searchDocToString(doc: SearchDoc): string {
	let output = "";
	doc.descendants(node => {
		if (node.type.name === "tag") {
			output += `#${node.textContent.slice(1)}#`;
			return false;
		} else if (node.text != null) {
			output += node.text.replace(/(\\|#)/g, "\\$1");
			return false;
		}
		return true;
	});
	return output;
}

/**
 * Convert a search string to a prose document for manipulation and rendering
 *
 * @see {@link searchDocToString}
 */
export function searchStringToDoc(input: string): SearchDoc {
	const markerFinder = /(\\*)#/g;
	let markers = [];
	// let startsWithTag = false;
	let contents = [];

	let match;
	while ((match = markerFinder.exec(input)) !== null) {
		// If there's an even number of backslashes (including zero), they're escaping
		// each other, and not the hash. That means it's a tag delineator.
		if (match[1].length % 2 === 0) {
			markers.push(match.index + match[1].length); // the index of the hash
		}
	}

	let cursor = 0;
	for (let i = 0; i < markers.length; i++) {
		const marker = markers[i];
		contents.push(input.slice(cursor, marker));
		cursor = marker + 1; // don't include the delineators in the content
	}

	contents.push(input.slice(cursor));

	const nodes = contents
		.map((e, i) => {
			const isTag = i % 2 === 1;
			const unescaped = e.replace(/\\(\\|#)/g, "$1");
			return isTag ? ["tag", `#${unescaped}`] : ["text", unescaped];
		})
		.filter(([, content]) => content.length > 0);

	return searchSchema.node(
		"doc",
		undefined,
		nodes.map(([type, content]) =>
			type === "tag" ? searchSchema.node("tag", undefined, searchSchema.text(content)) : searchSchema.text(content),
		),
	);
}

/**
 * For a SearchDoc and a list of tags, get suggested tags for the last word in the SearchDoc.
 * @returns A list of tuples, each containing: the suggested tag's label, a doc with the last word in the query replaced
 * 	with the tag, and the match score
 */
export function getTagSuggestions(queryDoc: SearchDoc, tags: readonly { color: string; label: string }[], limit = 6) {
	const { lastChild } = queryDoc;
	if (!lastChild || lastChild.type.name !== "text") return [];
	const lastWord = lastChild.text?.match(/#?\b\w+$/u);
	if (!lastWord) return [];
	let lastWordClean = lastWord[0].replace("#", "");
	const matches = tags
		.map(tag => [multiScore(tag.label, lastWordClean), tag] as const)
		.filter((scored): scored is readonly [number, (typeof scored)[1]] => typeof scored[0] === "number")
		.sort(([a], [b]) => b - a)
		.slice(0, limit);
	return matches.map(([score, tag]) => {
		return [
			tag.label,
			queryDoc.replace(
				queryDoc.content.size - lastWord[0].length,
				queryDoc.content.size,
				new Slice(
					Fragment.from(searchSchema.node("tag", { color: tag.color }, searchSchema.text(`#${tag.label}`))),
					0,
					0,
				),
			),
			score,
		] as const;
	});
}

class SearchEditorExtension extends Extension {
	constructor(options: { onSubmit?: () => void }) {
		super(options);
	}

	get name() {
		return "SearchEditor";
	}

	get plugins() {
		return [
			new Plugin({
				props: {
					handleDOMEvents: {
						keydown: (_view, event) => {
							/* Using `handleDOMEvents` instead of `handleKeyDown` means we can stop ProseMirror
							 * doing anything in response to the key press, including adding a line break to the
							 * document, *without* running `preventDefault` on the DOM event, meaning
							 * `KeyboardNavigation` will still respond to it. */
							if (event.key === "Enter") {
								this.options.onSubmit?.();
								return true;
							}
							if (event.key === "Escape") return true;
							return false;
						},
					},
					attributes: {
						enterKeyHint: "go",
					},
				},
			}),
		];
	}
}

interface SearchFormProps {
	value: string;
	placeholder: string;
	onChange: (value: string) => void;
	onClear: (event?: React.MouseEvent<HTMLAnchorElement>) => void;
	onSearch?: (query: string) => void;
	useClearIcon?: boolean;
	className?: string;
	disabled?: boolean;
	focusOnMount?: boolean;
}

const SearchForm = React.forwardRef<ProseReact, SearchFormProps>(function SearchForm(props, ref) {
	const editorRef = useForwardedRef<ProseReact>(ref);

	const { value, onSearch, disabled, onChange: onChangeProp } = props;
	const submitForm = useCallback(() => onSearch?.(value), [onSearch, value]);
	const onSearchRef = useRef(submitForm);
	onSearchRef.current = submitForm;

	const proseConfig = useMemo(
		() =>
			new EditorConfig({
				nodes: { doc: { content: "inline*" } },
				extensions: [
					new TextNode(),
					new SearchEditorExtension({
						// This exists because you can't update a Prose editor's config after it's already initialized, which
						// means you can't switch out a function handler, and `useCallback` relies on being able to just
						// re-define a function whenever its bound variable changes. So we use a ref to continuously mutate
						// `onSearchRef` without re-defining `onSubmit`
						onSubmit: (...args: Parameters<(typeof onSearchRef)["current"]>) => onSearchRef.current?.(...args),
					}),
					new GlobalPlaceholder({ text: props.placeholder ?? "Search" }),
				],
			}),
		[], // eslint-disable-line react-hooks/exhaustive-deps
		// Changing the EditorConfig after rendering the editor doesn't work, so there's
		// no use including dependencies here
	);

	const doc = React.useMemo(() => searchStringToDoc(value), [value]);

	const handleClear = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		if (!editorRef.current?.editorView) throw new Error("Tried to clear a Search Form but found no editor view");
		const editorView = editorRef.current.editorView;
		const { state } = editorView;
		let tr = state.tr.replace(0, state.doc.content.size, new Slice(Fragment.empty, 0, 0));
		editorView.dispatch(tr);
		editorRef.current?.focus();
		props.onClear(event);
	};

	const handleFormSubmit = useCallback(
		(event: React.FormEvent) => {
			event.stopPropagation();
			submitForm();
		},
		[submitForm],
	);

	const onChange = useCallback(
		(state: EditorState) => {
			if (disabled) return;
			onChangeProp(searchDocToString(state.doc));
		},
		[disabled, onChangeProp],
	);

	return (
		<Form
			onSubmit={handleFormSubmit}
			className={cx("search-form", props.className)}
			data-test-id="search-form"
			autoComplete="off"
		>
			<Form.Field>
				<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />

				<div className="prose-editor">
					<ProseReact
						focusOnMount={props.focusOnMount}
						ref={editorRef}
						controlledDoc={doc}
						resetSelectionOnControlledDocChange={false}
						config={proseConfig}
						onChange={onChange}
					/>
				</div>
				{props.value.length > 0 ? (
					<a href="#" onClick={handleClear} className="clear-button">
						{props.useClearIcon ? (
							<FontAwesomeIcon icon={faTimes} />
						) : (
							<>
								<FontAwesomeIcon icon={faTimes} className="clear-button-icon" />
								<span className="clear-button-text">clear</span>
							</>
						)}
					</a>
				) : null}
			</Form.Field>
		</Form>
	);
});

export default SearchForm;
